// Chrome's « this site can be installed » event, kept until the owner taps
// « Install » on the card (Android + desktop Chrome/Edge; iPhone has no such
// event — the card shows the Share-sheet steps there instead).
//
// ⚠️ ATTACHED AT BOOT (main.tsx), NOT IN THE CARD. Chrome fires
// `beforeinstallprompt` once per page load, whenever its own checks finish —
// often while the owner is on another admin page. A listener that only exists
// while the card is mounted would miss it, and the card would never appear.

/** Not in lib.dom: Chrome-only. */
export interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>;
  readonly userChoice: Promise<{ outcome: 'accepted' | 'dismissed'; platform?: string }>;
}

export interface InstallPromptState {
  /** The saved event, or null when there is nothing to replay. */
  event: BeforeInstallPromptEvent | null;
  /** Installed during this visit (accepted prompt or `appinstalled`). */
  installed: boolean;
}

export function createInstallPromptStore() {
  let state: InstallPromptState = { event: null, installed: false };
  const listeners = new Set<() => void>();
  const set = (next: InstallPromptState) => {
    state = next;
    listeners.forEach((l) => l());
  };
  let attached = false;

  return {
    /** Start listening on `win`. Safe to call more than once. */
    attach(win: EventTarget): void {
      if (attached) return;
      attached = true;
      win.addEventListener('beforeinstallprompt', (e) => {
        // Our card replaces Chrome's own mini-infobar.
        e.preventDefault();
        set({ ...state, event: e as BeforeInstallPromptEvent });
      });
      win.addEventListener('appinstalled', () => set({ event: null, installed: true }));
    },
    getSnapshot: (): InstallPromptState => state,
    subscribe(listener: () => void): () => void {
      listeners.add(listener);
      return () => { listeners.delete(listener); };
    },
    /** Show Chrome's install dialog. Must run inside the tap's handler. */
    async prompt(): Promise<'accepted' | 'dismissed' | 'unavailable'> {
      const e = state.event;
      if (!e) return 'unavailable';
      // An event can be prompted once; drop it before awaiting.
      set({ ...state, event: null });
      await e.prompt();
      const { outcome } = await e.userChoice;
      if (outcome === 'accepted') set({ event: null, installed: true });
      return outcome;
    },
  };
}

export type InstallPromptStore = ReturnType<typeof createInstallPromptStore>;

/** The app's one store — main.tsx attaches it to `window` at boot. */
export const installPrompt = createInstallPromptStore();
